// Cell outline geometry (architecture §9.3, visuals §3):
// - classic: agar's two-fill band. The polygon (or circle) is filled at `r + off_i + 5` in the
//   outline colour, then at `r + off_i − 5` in the cell colour: a 10 u band centred on the radius,
//   constant in world units, no stroke. `off` comes from the jelly field (render/jelly.ts), or is
//   null for a perfect two-circle band (Low / Very low, tiny cells);
// - sketch: the "boiling" ink outline, a polygon of N = clamp(round(r_screen / 4), 16, 120)
//   vertices whose radius is r × (1 + 0.012 × noise(entityId, vertex, boilFrame)), where
//   boilFrame advances every 125 ms (frozen at Medium and below and with Reduce motion), stroked.
//
// Pure geometry plus path tracing.

import type { Ctx2D } from './canvas.js';
import type { Wobble } from './noise.js';

const TAU = Math.PI * 2;

export const OUTLINE = Object.freeze({
  boilFrameMs: 125,
  boilAmplitude: 0.012,
  boil: { minVertices: 16, maxVertices: 120, pxPerVertex: 4 },
  sketchStroke: { perRadius: 0.05, minPx: 2, maxPx: 18 },
  classicStroke: { perRadius: 0.06, minPx: 2, maxPx: 14 },
});

/** Largest vertex count any outline uses (scratch buffer size). */
export const MAX_OUTLINE_VERTICES = 256;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Sketch outline vertex count: `clamp(round(rPx / 4), 16, 120)`. */
export function boilVertexCount(rPx: number): number {
  const b = OUTLINE.boil;
  return clamp(Math.round(rPx / b.pxPerVertex), b.minVertices, b.maxVertices);
}

function strokePx(rPx: number, p: { perRadius: number; minPx: number; maxPx: number }): number {
  // Tiny cells get a proportionally thin line so the outline never swallows the fill.
  const lo = Math.min(p.minPx, Math.max(0.5, rPx * 0.3));
  return clamp(p.perRadius * rPx, lo, p.maxPx);
}

/** Sketch outline width in screen px: `clamp(0.05·r·zoom, 2, 18)` (architecture §9.3). */
export function sketchStrokePx(rPx: number): number {
  return strokePx(rPx, OUTLINE.sketchStroke);
}

/** Legacy stroked outline width in screen px (sketch-style viruses, ejected): clamp(0.06·r_px, 2, 14). */
export function classicStrokePx(rPx: number): number {
  return strokePx(rPx, OUTLINE.classicStroke);
}

/** The boil frame at `nowMs`: one step every 125 ms. */
export function boilFrameAt(nowMs: number): number {
  return Math.floor(nowMs / OUTLINE.boilFrameMs);
}

interface Ring {
  readonly cos: Float64Array;
  readonly sin: Float64Array;
}

const rings = new Map<number, Ring>();

/** Unit vectors at `n` evenly spaced angles from 0 (cached per n). */
export function unitRing(n: number): Ring {
  let ring = rings.get(n);
  if (!ring) {
    const cos = new Float64Array(n);
    const sin = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const a = (i * TAU) / n;
      cos[i] = Math.cos(a);
      sin[i] = Math.sin(a);
    }
    ring = { cos, sin };
    if (rings.size > 512) rings.clear();
    rings.set(n, ring);
  }
  return ring;
}

/** Adds a circle sub-path. */
export function traceCircle(ctx: Ctx2D, x: number, y: number, r: number): void {
  ctx.moveTo(x + r, y);
  ctx.arc(x, y, r, 0, TAU);
}

/** Adds a closed polygon sub-path through `radii[i] − inset` at the `n` ring angles. */
export function traceRing(
  ctx: Ctx2D,
  x: number,
  y: number,
  radii: Float64Array,
  n: number,
  inset = 0,
): void {
  const { cos, sin } = unitRing(n);
  const r0 = Math.max(0, (radii[0] ?? 0) - inset);
  ctx.moveTo(x + r0 * (cos[0] ?? 1), y + r0 * (sin[0] ?? 0));
  for (let i = 1; i < n; i++) {
    const ri = Math.max(0, (radii[i] ?? 0) - inset);
    ctx.lineTo(x + ri * (cos[i] ?? 0), y + ri * (sin[i] ?? 0));
  }
  ctx.closePath();
}

/** Sketch boil radii: `out[i] = r × (1 + amplitude × wobble(θ_i))` for `n` vertices. */
export function boilRadii(
  out: Float64Array,
  n: number,
  r: number,
  wobble: Wobble,
  amplitude: number = OUTLINE.boilAmplitude,
): void {
  wobble.fill(out, n);
  for (let i = 0; i < n; i++) out[i] = r * (1 + amplitude * (out[i] ?? 0));
}

export interface CircleLike {
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

/** The classic band width in world units (visuals §3): outer r + 5, inner r − 5. */
export const BAND_U = 10;

/**
 * Adds a closed polygon sub-path through `r + off[i] + delta` at `n` evenly spaced angles from 0
 * (radii are clamped at 0).
 */
export function traceJelly(
  ctx: Ctx2D,
  x: number,
  y: number,
  r: number,
  off: Float64Array,
  n: number,
  delta: number,
): void {
  const { cos, sin } = unitRing(n);
  const base = r + delta;
  let rho = base + (off[0] ?? 0);
  if (rho < 0) rho = 0;
  ctx.moveTo(x + rho, y);
  for (let i = 1; i < n; i++) {
    rho = base + (off[i] ?? 0);
    if (rho < 0) rho = 0;
    ctx.lineTo(x + rho * (cos[i] ?? 0), y + rho * (sin[i] ?? 0));
  }
  ctx.closePath();
}

/**
 * agar's two-fill outline (visuals §3): fills the outer shape (`r + off + band/2`) in `border`,
 * then the inner shape (`r + off − band/2`) in `fill`. With `off` null (or `n` < 3) both are
 * circles. Leaves the inner shape as the current path (for a skin clip). `fill` / `border` are
 * CSS colour strings.
 */
export function fillBand(
  ctx: Ctx2D,
  x: number,
  y: number,
  r: number,
  off: Float64Array | null,
  n: number,
  fill: string,
  border: string,
  bandU: number = BAND_U,
): void {
  const h = bandU / 2;
  ctx.beginPath();
  if (off && n >= 3) traceJelly(ctx, x, y, r, off, n, h);
  else traceCircle(ctx, x, y, Math.max(0, r + h));
  ctx.fillStyle = border;
  ctx.fill();
  ctx.beginPath();
  if (off && n >= 3) traceJelly(ctx, x, y, r, off, n, -h);
  else traceCircle(ctx, x, y, Math.max(0, r - h));
  ctx.fillStyle = fill;
  ctx.fill();
}
